import React, { ReactNode } from 'react'
import { useField } from 'formik'
import clsx from 'clsx'
import {
  CloseCircleFilled,
  CheckCircleFilled,
  InfoCircleOutlined,
  SaveOutlined
} from '@ant-design/icons'
import { Upload } from 'antd'
import {
  RcFile as AntRcFile,
  UploadFile,
  UploadProps
} from 'antd/lib/upload/interface'

import {
  AutoComplete,
  AutoCompleteProps,
  Input,
  InputProps,
  Select,
  SelectProps,
  TextArea,
  TextAreaProps,
  Tooltip,
  TooltipProps
} from '../../atoms'
import { useShowMessage } from '../../providers'
import { getWordCount } from '@pepper/utils/src/function/misc'

import styles from './FormikFields.module.css'

import type { FieldProps } from './FormikFields.types'

/* text */
interface TextFieldProps extends FieldProps {
  fieldProps: InputProps & {
    tooltipProps?: TooltipProps
    inlineSave?: boolean
    handleInlineSave?: (name: string, value: number) => void
  }
  toolTip?: ReactNode
}

export const TextField: React.FC<TextFieldProps> = ({
  className = '',
  label,
  name,
  helperText,
  helperErrorText,
  showError = true,
  required,
  toolTip,
  fieldProps
}) => {
  const [field, meta] = useField(name)
  const errorClass = meta.touched && meta.error ? styles.borderRed : ''
  return (
    <div className={className}>
      <label>
        {label && (
          <span className={styles.labelText}>
            {label}
            {required && <span className={styles.requiredText}>*</span>}
            <Tooltip
              title={toolTip}
              placement="top"
              {...(fieldProps?.tooltipProps ? fieldProps.tooltipProps : {})}
            >
              {toolTip && <InfoCircleOutlined />}
            </Tooltip>
          </span>
        )}

        <Input
          {...field}
          {...fieldProps}
          className={`${errorClass}`}
          {...(fieldProps?.inlineSave && {
            suffix: (
              <span className={styles.inlineSave}>
                <SaveOutlined
                  color="#5440eb"
                  onClick={() => {
                    if (typeof fieldProps.handleInlineSave !== 'function') {
                      return
                    }

                    fieldProps?.handleInlineSave(name, field.value)
                  }}
                />
              </span>
            )
          })}
        />
      </label>

      {helperText && <span className={styles.helperText}>{helperText}</span>}
      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
      {helperErrorText && (
        <div className={styles.helperText}>
          {meta.error || field.value === '' ? (
            <CloseCircleFilled className={styles.red} />
          ) : (
            <CheckCircleFilled className={styles.green} />
          )}
          {helperErrorText}
        </div>
      )}
    </div>
  )
}

/* textarea */
interface TextAreaFieldProps extends FieldProps {
  fieldProps: TextAreaProps & { tooltipProps?: TooltipProps }
  toolTip?: ReactNode
}

export const TextAreaField: React.FC<TextAreaFieldProps> = ({
  className = '',
  label,
  name,
  showError = true,
  helperText,
  toolTip,
  required,
  fieldProps
}) => {
  const [field, meta, helpers] = useField<string>(name)

  return (
    <div className={className}>
      <label>
        <span className={styles.labelText}>
          {label}
          {required && <span className={styles.requiredText}>*</span>}
          <Tooltip
            title={toolTip}
            placement="top"
            {...(fieldProps?.tooltipProps ? fieldProps.tooltipProps : {})}
          >
            {toolTip && <InfoCircleOutlined />}
          </Tooltip>
        </span>

        <TextArea
          {...field}
          {...fieldProps}
          onBlur={() => helpers.setTouched(true)}
        />
      </label>
      {helperText && (
        <span className={clsx(styles.helperText, 'helper-text')}>
          {helperText}
        </span>
      )}
      <div className={clsx(styles.errorContainer, 'error-container')}>
        {showError && meta.touched && meta.error ? (
          <div className={styles.error}>
            <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
          </div>
        ) : (
          <div />
        )}
        {fieldProps?.showWordCount && (
          <span className={styles.textareaWordCount}>
            {getWordCount(field.value)}
          </span>
        )}
      </div>
    </div>
  )
}

/* select field */
interface SelectFieldProps extends FieldProps {
  fieldProps: SelectProps & {
    handleOnChange?: () => void
    textFormat?: (value: string[]) => string[]
  } & {
    tooltipProps?: TooltipProps
  }
  toolTip?: ReactNode
}

export const SelectField: React.FC<SelectFieldProps> = ({
  className = '',
  label,
  name,
  required,
  showError = true,
  helperText,
  toolTip,
  fieldProps
}) => {
  const [field, meta, helpers] = useField(name)

  return (
    <div className={`${className} ${styles.selectField}`}>
      <label>
        <span className={styles.labelText}>
          {label}
          {required && <span className={styles.requiredText}>*</span>}
          <Tooltip
            title={toolTip}
            placement="top"
            {...(fieldProps?.tooltipProps ? fieldProps.tooltipProps : {})}
          >
            {toolTip && <InfoCircleOutlined />}
          </Tooltip>
        </span>

        <Select
          className="w-100"
          optionFilterProp="label"
          onChange={(value): void => {
            const formattedValue = fieldProps.textFormat
              ? fieldProps.textFormat(value)
              : value
            helpers.setValue(formattedValue)
            fieldProps.handleOnChange?.()
          }}
          onBlur={() => helpers.setTouched(true)}
          value={field.value}
          {...fieldProps}
        />
      </label>
      {helperText && <span className={styles.helperText}>{helperText}</span>}
      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}

/* text */
interface UploadFieldProps extends FieldProps {
  fieldProps: UploadProps & {
    maxCount?: number
    maxSizePerFile?: number
  }
}

interface RcFile extends AntRcFile {
  flag?: boolean
}

export const UploadField: React.FC<UploadFieldProps> = ({
  className = '',
  label,
  name,
  helperText,
  showError = true,
  required,
  fieldProps
}) => {
  const showMessage = useShowMessage()
  const [field, meta, helper] = useField<UploadFile[]>(name)
  const errorClass = meta.touched && meta.error ? styles.borderRed : ''

  const handleBeforeUpload = (_: RcFile, fileList: RcFile[]) => {
    const maxCount = fieldProps?.maxCount ?? 1
    const maxSizeInMb = fieldProps?.maxSizePerFile ?? 1
    const validFileTypes = fieldProps?.accept

    if (field.value.length + fileList.length > maxCount) {
      fileList.forEach(_file => (_file.flag = true))
      const message = `You can only upload ${maxCount} files`
      showMessage({ type: 'error', message })
      return false
    }

    if (
      validFileTypes &&
      fileList.some(item => !validFileTypes.includes(item.type))
    ) {
      fileList.forEach(_file => (_file.flag = true))
      const message = `File type not supported`
      showMessage({ type: 'error', message })
      return false
    }

    if (fileList.some(item => item.size / 1024 / 1024 > maxSizeInMb)) {
      fileList.forEach(_file => (_file.flag = true))
      const message = `max file size limit reached`
      showMessage({ type: 'error', message })
      return false
    }

    return true
  }

  return (
    <div className={className}>
      <label>
        {label && (
          <span className={styles.labelText}>
            {label}
            {required && <span className={styles.requiredText}>*</span>}
          </span>
        )}
      </label>

      <Upload.Dragger
        {...fieldProps}
        beforeUpload={handleBeforeUpload}
        onChange={info => {
          // ignore file rejected by beforeUpload
          const files = info.fileList.filter(file => {
            if ('flag' in file && file.flag === true) return false
            return true
          })
          helper.setValue([...files])
        }}
        fileList={field.value}
        className={`${errorClass}`}
      >
        Upload files
      </Upload.Dragger>

      {helperText && <span className={styles.helperText}>{helperText}</span>}
      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}

interface AutoCompleteFieldProps extends FieldProps {
  fieldProps: AutoCompleteProps & {
    handleOnChange?: () => void
    textFormat?: (value: unknown) => unknown
  } & {
    tooltipProps?: TooltipProps
  }
  toolTip?: ReactNode
}

export const AutoCompleteField: React.FC<AutoCompleteFieldProps> = ({
  className = '',
  label,
  name,
  required,
  showError = true,
  helperText,
  toolTip,
  fieldProps
}) => {
  const [field, meta, helpers] = useField(name)

  return (
    <div className={`${className} ${styles.selectField}`}>
      <label>
        <span className={styles.labelText}>
          {label}
          {required && <span className={styles.requiredText}>*</span>}
          <Tooltip
            title={toolTip}
            placement="top"
            {...(fieldProps?.tooltipProps ? fieldProps.tooltipProps : {})}
          >
            {toolTip && <InfoCircleOutlined />}
          </Tooltip>
        </span>

        <AutoComplete
          className="w-100"
          optionFilterProp="label"
          onChange={(value): void => {
            const formattedValue = fieldProps.textFormat
              ? fieldProps.textFormat(value)
              : value
            helpers.setValue(formattedValue)
            fieldProps.handleOnChange?.()
          }}
          onBlur={() => helpers.setTouched(true)}
          value={field.value}
          {...fieldProps}
        />
      </label>
      {helperText && <span className={styles.helperText}>{helperText}</span>}
      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}
